import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMissatgeError } from '@/api/errors';
import { useCreaNode, useDesaNode, type RecursEstructura } from '@/api/ajustosEstructura';
import type { TipusArea } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Camp } from '@/pages/operacio/comu';
import { Calaix } from '../marc/Calaix';
import { opcionsArea, opcionsDepartament, useEstructura, type TipusNode } from '../marc/estructura';
import type { Seleccio } from './suport';
import { suggereixCodi } from './suport';

const RECURS: Record<TipusNode, RecursEstructura> = {
  DEPARTAMENT: 'departaments',
  AREA: 'arees',
  SERVEI: 'serveis',
};

/** «Tecnologia › Sistemes IT» com a llista de noms destacats. */
function Ruta({ noms }: { noms: string[] }) {
  return (
    <>
      {noms.map((n, i) => (
        <span key={`${n}-${i}`} className="hstack gap-6">
          {i > 0 ? <span aria-hidden="true">›</span> : null}
          <strong>{n}</strong>
        </span>
      ))}
    </>
  );
}

/**
 * Alta d'un departament, àrea o servei. El pare és sempre explícit (el node des d'on s'ha
 * clicat «＋»), i el calaix diu exactament on es crearà.
 */
export function NouNode({
  tipus,
  pareId,
  onTanca,
  onCreat,
}: {
  tipus: TipusNode;
  /** Departament (per a una àrea) o àrea (per a un servei). */
  pareId?: number | null;
  onTanca: () => void;
  onCreat: (s: Seleccio) => void;
}) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const missatgeError = useMissatgeError();
  const e = useEstructura();
  const crea = useCreaNode<{ id?: number | null } & Record<string, unknown>>(RECURS[tipus]);
  const [nom, setNom] = useState('');
  const [codi, setCodi] = useState('');
  const [tipusArea, setTipusArea] = useState<TipusArea>('GUARDIA_IT');
  const suggerit = suggereixCodi(nom);
  const codiFinal = (codi.trim() || suggerit).toUpperCase();

  const area = tipus === 'SERVEI' ? e.area(pareId) : undefined;
  const departament = tipus === 'AREA' ? e.departament(pareId) : e.departament(area?.departamentId);
  const on =
    tipus === 'DEPARTAMENT'
      ? null
      : [departament?.nom, tipus === 'SERVEI' ? area?.nom : null].filter((x): x is string => !!x);
  const pareValid = tipus === 'DEPARTAMENT' || pareId != null;
  const valid = nom.trim() !== '' && codiFinal !== '' && pareValid;

  const cos = () => {
    const base = { nom: nom.trim(), codi: codiFinal, actiu: true };
    if (tipus === 'AREA') return { ...base, departamentId: pareId, tipus: tipusArea };
    if (tipus === 'SERVEI') return { ...base, areaId: pareId, canalsDefecte: ['PUSH', 'EMAIL'] };
    return base;
  };

  return (
    <Calaix
      etiqueta={t(`estructura.crea.etiqueta.${tipus}`)}
      titol={t(`estructura.crea.titol.${tipus}`)}
      on={
        <>
          <span>{t('estructura.crea.on')}</span>
          {on ? <Ruta noms={on} /> : <strong>{t('estructura.crea.arrel')}</strong>}
        </>
      }
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('estructura.crea.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!valid || crea.isPending}
            onClick={() =>
              crea.mutate(cos(), {
                onSuccess: (desat) => {
                  if (desat?.id != null) onCreat({ tipus, id: desat.id });
                  onTanca();
                },
              })
            }
          >
            <Icona nom="checkmark" />
            {t(`estructura.crea.boto.${tipus}`)}
          </button>
        </>
      }
    >
      <Camp etiqueta={t('estructura.camps.nom')}>
        <input
          className="input"
          value={nom}
          placeholder={t(`estructura.crea.placeholder.${tipus}`)}
          onChange={(ev) => setNom(ev.target.value)}
        />
      </Camp>
      <Camp etiqueta={t('estructura.camps.codi')}>
        <input
          className="input mono"
          value={codi}
          placeholder={suggerit}
          onChange={(ev) => setCodi(ev.target.value.toUpperCase())}
        />
      </Camp>
      <span className="camp__ajuda">
        {suggerit && !codi.trim()
          ? t('estructura.crea.codiSuggerit', { codi: suggerit })
          : t('estructura.camps.codiAjuda')}
      </span>
      {tipus === 'AREA' ? (
        <div className="camp">
          <span className="camp__label">{t('estructura.camps.tipusArea')}</span>
          <div className="est-tipus" role="radiogroup" aria-label={t('estructura.camps.tipusArea')}>
            {(['GUARDIA_IT', 'CRISI'] as TipusArea[]).map((tp) => (
              <button
                key={tp}
                type="button"
                role="radio"
                aria-checked={tipusArea === tp}
                className="est-tipus__opcio"
                onClick={() => setTipusArea(tp)}
              >
                <strong>
                  <span className={`node-punt node-punt--${tp.toLowerCase()}`} aria-hidden="true" />
                  {tc(`tipusArea.${tp}`)}
                </strong>
                <span>{t(`estructura.ajudaTipus.${tp}`)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {tipus === 'SERVEI' && area ? (
        <div className="info-banda">
          <Icona nom="info-circle" />
          {t('estructura.crea.hereta', { tipus: tc(`tipusArea.${area.tipus}`) })}
        </div>
      ) : null}
      {crea.isError ? <p className="error-text">{missatgeError(crea.error)}</p> : null}
    </Calaix>
  );
}

/** Canvia una àrea de departament o un servei d'àrea. */
export function MouNode<T extends { id?: number | null; nom: string }>({
  tipus,
  node,
  pareActual,
  onTanca,
}: {
  tipus: 'AREA' | 'SERVEI';
  node: T & { departamentId?: number; areaId?: number };
  pareActual: string;
  onTanca: () => void;
}) {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const e = useEstructura();
  const desa = useDesaNode<T>(RECURS[tipus], node.id);
  const actual = tipus === 'AREA' ? node.departamentId : node.areaId;
  const [desti, setDesti] = useState<number | ''>('');
  const opcions = (tipus === 'AREA' ? opcionsDepartament(e) : opcionsArea(e)).filter(
    (o) => o.valor !== actual,
  );
  return (
    <Calaix
      etiqueta={t('estructura.mou.etiqueta')}
      titol={t('estructura.mou.titol', { nom: node.nom })}
      on={
        <>
          <span>{t('estructura.mou.ara')}</span>
          <strong>{pareActual}</strong>
        </>
      }
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('estructura.crea.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={desti === '' || desa.isPending}
            onClick={() =>
              desa.mutate(
                {
                  ...node,
                  ...(tipus === 'AREA' ? { departamentId: desti } : { areaId: desti }),
                } as T,
                { onSuccess: onTanca },
              )
            }
          >
            <Icona nom="arrow-right" />
            {t('estructura.mou.mou')}
          </button>
        </>
      }
    >
      <Camp etiqueta={t(`estructura.mou.${tipus}`)}>
        <Desplegable
          aria-label={t(`estructura.mou.${tipus}`)}
          valor={desti}
          opcions={opcions}
          onCanvia={(v) => setDesti(v)}
          cerca
        />
      </Camp>
      <p className="dim t-footnote" style={{ margin: 0 }}>
        {t('estructura.mou.text')}
      </p>
      {desa.isError ? <p className="error-text">{missatgeError(desa.error)}</p> : null}
    </Calaix>
  );
}
